/**
 * Shared rest-space micro detail for authored material shaders.
 *
 * Requires SURFACE_TRIPLANAR_GLSL then SURFACE_NOISE_GLSL before this block.
 *
 * The field is intentionally low amplitude and continuous. It is meant to
 * break up a clean macro surface at close range, not to replace a scanned map
 * or create silhouette displacement.
 */
export interface SurfaceMicroDetailSettings {
  /** Physical metres per fine detail feature. */
  scale: number;
  /** Height-normal strength, clamped by each material's quality budget. */
  strength: number;
  /** Roughness modulation amplitude. */
  roughnessVariation: number;
  seed: number;
}

export const DEFAULT_SURFACE_MICRO_DETAIL: SurfaceMicroDetailSettings = {
  scale: 0.045,
  strength: 0.12,
  roughnessVariation: 0.08,
  seed: 101,
};

export const SURFACE_MICRO_GLSL = /* glsl */ `
float surfaceMicroFbm(vec3 p, float seed, float baseWavelength, float fp) {
  float value = surfaceNoiseBL(p, seed * 17.17, baseWavelength, fp);
  value += surfaceNoiseBL(
    p * 2.03 + vec3(17.0, 43.0, 29.0),
    (seed + 11.0) * 17.17,
    baseWavelength / 2.03,
    fp
  ) * 0.45;
  value += surfaceNoiseBL(
    p * 4.07 + vec3(31.0, 11.0, 47.0),
    (seed + 23.0) * 17.17,
    baseWavelength / 4.07,
    fp
  ) * 0.2;
  return value / 1.65;
}

float surfaceMicroFade(vec3 restPos, float metersPerFeature) {
  float feature = max(metersPerFeature, 0.002);
  return surfaceOctaveFade(feature / (1.8 * 4.07), surfacePixelFootprint(restPos));
}

float surfaceMicroField(vec3 restPos, float metersPerFeature, float seed) {
  float fp = surfacePixelFootprint(restPos);
  float feature = max(metersPerFeature, 0.002);
  vec3 p = restPos / feature;
  float broad = surfaceMicroFbm(p * 0.55 + vec3(5.0, 13.0, 7.0), seed, feature / 0.55, fp);
  float fine = surfaceMicroFbm(p * 1.8 + vec3(19.0, 3.0, 31.0), seed + 37.0, feature / 1.8, fp);
  return mix(broad, fine, 0.38);
}

float surfaceMicroHeight(vec3 restPos, float metersPerFeature, float seed) {
  return 0.5 + (surfaceMicroField(restPos, metersPerFeature, seed) - 0.5) * 0.055;
}

vec3 surfaceMicroNormal(
  vec3 restN,
  vec3 restPos,
  float metersPerFeature,
  float strength,
  float seed
) {
  float fade = surfaceMicroFade(restPos, metersPerFeature);
  return surfaceHeightRestNormal(
    restN,
    restPos,
    surfaceMicroHeight(restPos, metersPerFeature, seed),
    max(strength, 0.0) * fade
  );
}
`;
